import React, { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import { hexbin as d3Hexbin } from "d3-hexbin";
import * as topojson from "topojson-client";
import { WidgetContainer } from "./shared/widgetContainer/widgetContainer";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { DownloadExcel } from "../functions/downloadExcel";

const HexWorldMap = ({ instanceData = [], onHexClick = () => {} }) => {
  const theme = useTheme();
  const svgRef = useRef();
  const [activeAccount, setActiveAccount] = useState("ALL");
  const [hoveredHex, setHoveredHex] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const width = 960;
  const height = 500;
  const radius = 7;
  const bgColor = theme.palette.background.land;
  const landHexColor = theme.palette.background.landCover;

  const safeData = Array.isArray(instanceData) ? instanceData : [];

  const accountList = useMemo(() => {
    const uniqueAccounts = new Set(
      safeData.map((inst) => inst.awsAccountName).filter((a) => a && a !== "ALL")
    );
    return ["ALL", ...uniqueAccounts];
  }, [safeData]);

  const filteredData = useMemo(() => {
    const grouped = {};
    const source =
      activeAccount === "ALL"
        ? safeData.filter((d) => d.awsAccountName === "ALL")
        : safeData.filter((d) => d.awsAccountName === activeAccount);

    source.forEach((d) => {
      const key = `${d.lat},${d.lon}`;
      if (!grouped[key]) {
        grouped[key] = { ...d, count: 0, accounts: {}, osCounts: {} };
      }
      grouped[key].count += d.count;
      if (activeAccount !== "ALL") {
        grouped[key].accounts = d.accounts;
        grouped[key].osCounts = d.osCounts;
      } else {
        grouped[key].osCounts = d.osCounts;
      }
    });

    return Object.values(grouped);
  }, [activeAccount, safeData]);

  useEffect(() => {
    const projection = d3
      .geoMercator()
      .scale(160)
      .center([5, 25])
      .translate([width / 2, height / 2]);

    const hexbin = d3Hexbin()
      .extent([
        [0, 0],
        [width, height],
      ])
      .radius(radius);

    const svg = d3.select(svgRef.current);
    svg.selectAll(".hex-layer").remove();
    svg.selectAll(".overlay-layer").remove();

    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", bgColor);

    d3.json("/maps/world50m.json").then((worldData) => {
      const land = topojson.feature(worldData, worldData.objects.land);

      if (!svg.select(".land-hexes").node()) {
        const offCanvas = document.createElement("canvas");
        offCanvas.width = width;
        offCanvas.height = height;
        const ctx = offCanvas.getContext("2d");

        const landPath = d3.geoPath(projection, ctx);
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        landPath(land);
        ctx.fill();

        const landData = ctx.getImageData(0, 0, width, height).data;

        const grid = [];
        const stepX = radius * Math.sqrt(3);
        const stepY = radius * 1.5;

        for (let x = 0; x <= width; x += stepX) {
          for (let y = 0; y <= height; y += stepY) {
            const index = (Math.floor(y) * width + Math.floor(x)) * 4;
            const red = landData[index];
            if (red > 200) {
              grid.push([x, y]);
            }
          }
        }

        const hexes = hexbin(grid);

        svg
          .append("g")
          .attr("class", "land-hexes")
          .selectAll("path.hex")
          .data(hexes)
          .enter()
          .append("path")
          .attr("class", "hex")
          .attr("d", hexbin.hexagon())
          .attr("transform", (d) => `translate(${d.x},${d.y})`)
          .style("fill", landHexColor)
          .style("stroke", "#404040")
          .style("stroke-width", "0.3px");
      }

      const overlay = svg.append("g").attr("class", "overlay-layer");

      filteredData.forEach(({ lat, lon, color, count, regionText, accounts, osCounts }) => {
        const [x, y] = projection([lon, lat]);
        if (!x || !y) return;

        overlay
          .append("path")
          .attr("d", hexbin.hexagon())
          .attr("transform", `translate(${x},${y}) scale(0)`)
          .style("fill", color)
          .style("cursor", "pointer")
          .style("stroke", "#ffffff")
          .style("stroke-width", "0.5px")
          .on("mouseenter", (event) => {
            setHoveredHex({
              x,
              y,
              regionText,
              accounts,
              osCounts,
            });
            setTooltipPos({ x: event.offsetX + 20, y: event.offsetY + 60 });
          })
          .on("mousemove", (event) => {
            setTooltipPos({ x: event.offsetX + 20, y: event.offsetY + 60 });
          })
          .on("mouseleave", () => setHoveredHex(null))
          .on("click", () => onHexClick({ regionText, accounts }))
          .transition()
          .duration(400)
          .attr("transform", `translate(${x},${y}) scale(${Math.max(3, Math.sqrt(count) / 5)})`);

        overlay
          .append("text")
          .attr("x", x)
          .attr("y", y + 4)
          .text(count)
          .attr("text-anchor", "middle")
          .style("font-size", `${Math.max(10, Math.sqrt(count) / 2)}px`)
          .style("fill", "#ffffff")
          .style("font-weight", "bold")
          .style("pointer-events", "none")
          .style("opacity", 0)
          .transition()
          .duration(400)
          .style("opacity", 1);
      });
    });
  }, [filteredData]);

  return (
    <WidgetContainer
      title="EC2 Regions"
      description="EC2 region volumes by product"
      loaded={instanceData}
      maximize={true}
      download={true}
      maximized={() => {
        if (accountList.length > 1) {
          setActiveAccount((prev) => (prev === accountList[0] ? accountList[1] : accountList[0]));
        }
      }}
      onDownload={() => {
        const excluded = ["lat", "lon", "color", "osCounts", "accounts", "RegionText"];

        const filtered = instanceData.map((row) => {
          const cleaned = {};

          Object.entries(row).forEach(([key, value]) => {
            if (!excluded.includes(key)) {
              const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
              cleaned[capitalizedKey] = value;
            }
          });

          return cleaned;
        });

        DownloadExcel("EC2 Regions", filtered);
      }}
    >
      <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />

      {hoveredHex && (
        <Box
          sx={{
            position: "absolute",
            left: tooltipPos.x,
            top: tooltipPos.y,
            backgroundColor: "#1E1E1E",
            color: "#fff",
            padding: "8px 12px",
            borderRadius: "6px",
            pointerEvents: "none",
            zIndex: 100,
            boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
            fontSize: "12px",
            maxWidth: "250px",
          }}
        >
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>{hoveredHex.regionText}</Typography>

          {activeAccount === "ALL"
            ? Object.entries(hoveredHex.osCounts).map(([os, count]) => (
                <Typography key={os}>{`${os}: ${count}`}</Typography>
              ))
            : Object.entries(hoveredHex.osCounts[activeAccount] || {}).map(([os, count]) => (
                <Typography key={os}>{`${os}: ${count}`}</Typography>
              ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        {accountList.map((account) => (
          <IconButton
            key={account}
            size="small"
            onClick={() => setActiveAccount(account)}
            sx={{
              borderRadius: 2,
              fontSize: 10,
              fontWeight: "bold",
              backgroundColor: activeAccount === account ? "rgba(255,255,255,0.1)" : "transparent",
              color: activeAccount === account ? "#fff" : "rgba(255,255,255,0.7)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            {account}
          </IconButton>
        ))}
      </Box>
    </WidgetContainer>
  );
};

export default React.memo(HexWorldMap);
